import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "throttle-debounce";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { StorageScope } from "../../../services/api/instances/storageApi/types/service_types";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useRenameStorageObject = (service: StorageScope) => {
  const dispatch = useAppDispatch();
  const { selectedId, selectedObject } = useStorageSelectors(service);
  const [name, setName] = useState<string>(selectedObject?.friendly_name ?? "");

  const selectedIdRef = useRef(selectedId);
  const selectedObjectRef = useRef(selectedObject);

  selectedIdRef.current = selectedId;
  selectedObjectRef.current = selectedObject;

  const updateName = useCallback(
    debounce(1000, (name) => {
      const id = selectedIdRef.current;
      const object = selectedObjectRef.current;
      if (name === "" && object?.friendly_name) {
        dispatch(
          createNotification("warning", "Имя объекта не может быть пустым"),
        );
        setName(object?.friendly_name);
        return;
      }
      if (!id || !object) return;
      dispatch(
        editStorageObject({
          id,
          name,
          value: object.value,
          service,
        }),
      );
    }),
    [dispatch],
  );

  useEffect(() => {
    setName(selectedObject?.friendly_name ?? "");
  }, [selectedObject?.friendly_name]);

  const updateObjectName = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newName = event.target.value;
      setName(newName);
      updateName(newName);
    },
    [updateName],
  );

  return {
    name,
    setName,
    selectedObject,
    updateObjectName,
  };
};
